/**
 * Best-effort detection of an iOS or Android mobile device.
 *
 * The web SPA is built and tuned for desktop; phones (and touch iPads)
 * get steered to the native app via the mobile gate in
 * `ProtectedRoute`. We intentionally key off the user agent — a *device*
 * fact — rather than viewport width: a narrow browser window on a laptop
 * is still "a computer" and must not be gated.
 */
export type MobilePlatform = 'ios' | 'android';

export function getMobilePlatform(): MobilePlatform | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';

  if (/android/i.test(ua)) return 'android';

  // iPhone / iPod, plus the classic iPad user-agent string.
  if (/iphone|ipod|ipad/i.test(ua)) return 'ios';

  // iPadOS 13+ masquerades as desktop Safari ("Macintosh") but reports
  // multiple touch points — the only reliable tell that it's a tablet.
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return 'ios';
  }

  return null;
}

export function isMobileDevice(): boolean {
  return getMobilePlatform() !== null;
}
